import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { websiteService } from '../websiteService';

// ── Services ────────────────────────────────────────────────
export function useWebsiteServices(page = 1, limit = 20, search?: string, status?: string) {
  return useQuery({
    queryKey: ['website-services', page, limit, search, status],
    queryFn: () => websiteService.getServices(page, limit, search, status),
    placeholderData: (previous) => previous,
  });
}

export function useWebsiteService(id?: string) {
  return useQuery({
    queryKey: ['website-service', id],
    queryFn: () => websiteService.getService(id!),
    enabled: !!id,
  });
}

export function useCreateWebsiteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: websiteService.createService,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-services'] }),
  });
}

export function useUpdateWebsiteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      websiteService.updateService(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-services'] }),
  });
}

export function useDeleteWebsiteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteService.deleteService(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-services'] }),
  });
}

export function useBulkDeleteWebsiteServices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => websiteService.bulkDeleteServices(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-services'] }),
  });
}

// ── Blogs ───────────────────────────────────────────────────
export function useWebsiteBlogs(
  page = 1,
  limit = 20,
  search?: string,
  status?: string,
  categoryId?: string,
) {
  return useQuery({
    queryKey: ['website-blogs', page, limit, search, status, categoryId],
    queryFn: () => websiteService.getBlogs(page, limit, search, status, categoryId),
    placeholderData: (previous) => previous,
  });
}

export function useWebsiteBlog(id?: string) {
  return useQuery({
    queryKey: ['website-blog', id],
    queryFn: () => websiteService.getBlog(id!),
    enabled: !!id,
  });
}

export function useCreateWebsiteBlog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: websiteService.createBlog,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-blogs'] }),
  });
}

export function useUpdateWebsiteBlog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => websiteService.updateBlog(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-blogs'] }),
  });
}

export function useDeleteWebsiteBlog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteService.deleteBlog(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-blogs'] }),
  });
}

export function useBulkDeleteWebsiteBlogs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => websiteService.bulkDeleteBlogs(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-blogs'] }),
  });
}

// ── Blog taxonomy (categories + tags) ───────────────────────
export function useBlogCategories(includeInactive = true) {
  return useQuery({
    queryKey: ['website-blog-categories', includeInactive],
    queryFn: () => websiteService.getBlogCategories(includeInactive),
  });
}

export function useCreateBlogCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: websiteService.createBlogCategory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-blog-categories'] });
      qc.invalidateQueries({ queryKey: ['website', 'blog-meta'] });
    },
  });
}

export function useUpdateBlogCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      websiteService.updateBlogCategory(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-blog-categories'] });
      qc.invalidateQueries({ queryKey: ['website', 'blog-meta'] });
    },
  });
}

export function useDeleteBlogCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteService.deleteBlogCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-blog-categories'] });
      qc.invalidateQueries({ queryKey: ['website', 'blog-meta'] });
    },
  });
}

export function useBlogTags(includeInactive = true) {
  return useQuery({
    queryKey: ['website-blog-tags', includeInactive],
    queryFn: () => websiteService.getBlogTags(includeInactive),
  });
}

export function useCreateBlogTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: websiteService.createBlogTag,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-blog-tags'] });
      qc.invalidateQueries({ queryKey: ['website', 'blog-meta'] });
    },
  });
}

export function useUpdateBlogTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => websiteService.updateBlogTag(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-blog-tags'] });
      qc.invalidateQueries({ queryKey: ['website', 'blog-meta'] });
    },
  });
}

export function useDeleteBlogTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteService.deleteBlogTag(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['website-blog-tags'] });
      qc.invalidateQueries({ queryKey: ['website', 'blog-meta'] });
    },
  });
}

export function useUploadWebsiteImage() {
  return useMutation({
    mutationFn: (file: File) => websiteService.uploadImage(file),
  });
}

// ── Gallery ─────────────────────────────────────────────────
export function useWebsiteGallery(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    status?: string;
  } = {},
) {
  const { page = 1, limit = 24, search = '', type = '', status = '' } = params;
  return useQuery({
    queryKey: ['website-gallery', page, limit, search, type, status],
    queryFn: () =>
      websiteService.getGalleryItems(
        page,
        limit,
        search || undefined,
        type || undefined,
        status || undefined,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateGalleryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: websiteService.createGalleryItem,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-gallery'] }),
  });
}

export function useUpdateGalleryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      websiteService.updateGalleryItem(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-gallery'] }),
  });
}

export function useDeleteGalleryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteService.deleteGalleryItem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-gallery'] }),
  });
}

export function useBulkDeleteGalleryItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => websiteService.bulkDeleteGalleryItems(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-gallery'] }),
  });
}

// ── Testimonials ────────────────────────────────────────────
export function useWebsiteTestimonials(
  params: { page?: number; limit?: number; search?: string; status?: string } = {},
) {
  const { page = 1, limit = 20, search = '', status = '' } = params;
  return useQuery({
    queryKey: ['website-testimonials', page, limit, search, status],
    queryFn: () =>
      websiteService.getTestimonials(page, limit, search || undefined, status || undefined),
    placeholderData: (previous) => previous,
  });
}

export function useCreateTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: websiteService.createTestimonial,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-testimonials'] }),
  });
}

export function useUpdateTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      websiteService.updateTestimonial(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-testimonials'] }),
  });
}

export function useDeleteTestimonial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteService.deleteTestimonial(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-testimonials'] }),
  });
}

export function useBulkDeleteTestimonials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => websiteService.bulkDeleteTestimonials(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-testimonials'] }),
  });
}

// ── Plans ───────────────────────────────────────────────────
export function useWebsitePlans(
  params: { page?: number; limit?: number; search?: string; status?: string } = {},
) {
  const { page = 1, limit = 20, search = '', status = '' } = params;
  return useQuery({
    queryKey: ['website-plans', page, limit, search, status],
    queryFn: () => websiteService.getPlans(page, limit, search || undefined, status || undefined),
    placeholderData: (previous) => previous,
  });
}

export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: websiteService.createPlan,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-plans'] }),
  });
}

export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => websiteService.updatePlan(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-plans'] }),
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => websiteService.deletePlan(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-plans'] }),
  });
}

export function useBulkDeletePlans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => websiteService.bulkDeletePlans(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-plans'] }),
  });
}

// ── Contact ─────────────────────────────────────────────────
export function useWebsiteContact() {
  return useQuery({
    queryKey: ['website-contact'],
    queryFn: () => websiteService.getContact(),
  });
}

export function useUpdateWebsiteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof websiteService.updateContact>[0]) =>
      websiteService.updateContact(data),
    meta: { successMessage: 'Contact details saved' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['website-contact'] }),
  });
}
